import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import type { GoogleAuthRequest, MicrosoftAuthRequest, AuthResponse } from '@/types/auth';
import type { ApiError } from '@/lib/api';
import { environment } from '@/config/environment';

export const loginWithGoogle = () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI;
  
  if (!clientId || !redirectUri) {
    console.error('Google OAuth configuration missing:', { clientId: !!clientId, redirectUri: !!redirectUri });
    throw new Error('Google OAuth not properly configured');
  }

  const scope = 'email profile';
  const responseType = 'code';
  const accessType = 'offline';
  const state = 'google';

  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.append('client_id', clientId);
  authUrl.searchParams.append('redirect_uri', redirectUri);
  authUrl.searchParams.append('response_type', responseType);
  authUrl.searchParams.append('scope', scope);
  authUrl.searchParams.append('access_type', accessType);
  authUrl.searchParams.append('state', state);

  console.group('🟢 Google OAuth Login Initiated');
  console.log('🚀 Redirect URI being sent to Google:', redirectUri);
  console.log('🔗 Environment redirect URI:', import.meta.env.VITE_GOOGLE_REDIRECT_URI);
  console.log('🌐 Current window location:', {
    origin: window.location.origin,
    pathname: window.location.pathname,
    href: window.location.href
  });
  console.log('📝 OAuth URL:', authUrl.toString());
  console.groupEnd();

  window.location.href = authUrl.toString();
};

export const loginWithMicrosoft = () => {
  // Validate required environment variables
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_MICROSOFT_REDIRECT_URI;
  const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID;

  if (!clientId) {
    console.error('Microsoft client ID is not configured. Please set VITE_MICROSOFT_CLIENT_ID in your .env file');
    return;
  }

  if (!redirectUri) {
    console.error('Microsoft redirect URI is not configured. Please set VITE_MICROSOFT_REDIRECT_URI in your .env file');
    return;
  }

  if (!tenantId) {
    console.error('Microsoft tenant ID is not configured. Please set VITE_MICROSOFT_TENANT_ID in your .env file');
    return;
  }

  const scope = 'user.read email profile openid';
  const responseType = 'code';
  const state = 'microsoft';

  // Use tenant-specific endpoint instead of /common
  const authUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
  authUrl.searchParams.append('client_id', clientId);
  authUrl.searchParams.append('redirect_uri', redirectUri);
  authUrl.searchParams.append('response_type', responseType);
  authUrl.searchParams.append('scope', scope);
  authUrl.searchParams.append('state', state);
  authUrl.searchParams.append('response_mode', 'query');

  console.group('🔵 Microsoft OAuth Login Initiated');
  console.log('🚀 Redirect URI being sent to Microsoft:', redirectUri);
  console.log('🔗 Environment redirect URI:', import.meta.env.VITE_MICROSOFT_REDIRECT_URI);
  console.log('🌐 Current window location:', {
    origin: window.location.origin,
    pathname: window.location.pathname,
    href: window.location.href
  });
  console.log('📝 OAuth URL:', authUrl.toString());
  console.groupEnd();

  window.location.href = authUrl.toString();
};

// OAuth callback handlers
class OAuthClient {
  private static async makeRequest<T>(endpoint: string, body: any): Promise<T> {
    const url = `${environment.apiPrefix.rootPath}${endpoint}`;
    
    console.group(`🌐 API Request: ${endpoint}`);
    console.log('📤 Request details:', {
      url,
      method: 'POST',
      timestamp: new Date().toISOString(),
      bodyPreview: {
        code: body.code ? `${body.code.substring(0, 30)}...` : 'NULL',
        redirect_uri: body.redirect_uri
      }
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      console.log('📥 Response status:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        let errorDetails;
        try {
          const errorText = await response.text();
          console.error('❌ API Error Response Body:', errorText);
          
          // Try to parse as JSON, fall back to text
          try {
            errorDetails = JSON.parse(errorText);
            console.error('❌ Parsed Error Details:', errorDetails);
          } catch {
            errorDetails = { message: errorText };
          }
        } catch (readError) {
          console.error('❌ Failed to read error response:', readError);
          errorDetails = { message: 'Unknown error' };
        }
        
        console.groupEnd();
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorDetails.message || 'Unknown error'}`);
      }

      const responseData = await response.json();
      console.log('✅ API Response Success:', {
        hasToken: !!responseData.token,
        hasRefreshToken: !!responseData.refreshToken,
        hasUser: !!responseData.user,
        userEmail: responseData.user?.email,
        responseKeys: Object.keys(responseData)
      });
      
      console.groupEnd();
      return responseData;
    } catch (error) {
      console.error('💥 API Request Failed:', error);
      console.groupEnd();
      throw error;
    }
  }

  static async handleGoogleCallback(code: string, redirectUri: string): Promise<AuthResponse> {
    console.group('🟢 Google OAuth Callback Handler');
    console.log('📝 Input parameters:', { 
      code: code.substring(0, 30) + '...', 
      redirectUri, 
      apiBaseUrl: environment.apiPrefix.rootPath 
    });
    
    // Enhanced redirect URI debugging
    console.log('🔗 Redirect URI Analysis:', {
      providedRedirectUri: redirectUri,
      currentWindowLocation: window.location.href,
      envRedirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI,
      urlMatch: redirectUri === import.meta.env.VITE_GOOGLE_REDIRECT_URI,
      windowOrigin: window.location.origin,
      windowPathname: window.location.pathname,
      constructedUri: window.location.origin + window.location.pathname
    });
    
    const request: GoogleAuthRequest = {
      code,
      redirect_uri: redirectUri,
    };

    console.log('📤 Request payload structure:', {
      code: !!request.code,
      redirect_uri: request.redirect_uri,
      codeLength: request.code.length
    });

    try {
      const authResponse = await OAuthClient.makeRequest<AuthResponse>('api/Auth/oauth/google', request);
      console.log('✅ Google OAuth Handler Success:', { 
        hasToken: !!authResponse.token, 
        hasUser: !!authResponse.user,
        userEmail: authResponse.user?.email 
      });
      
      console.groupEnd();
      return authResponse;
    } catch (error) {
      console.error('❌ Google OAuth Handler Error:', error);
      console.groupEnd();
      throw error;
    }
  }

  static async handleMicrosoftCallback(code: string, redirectUri: string): Promise<AuthResponse> {
    console.group('🔵 Microsoft OAuth Callback Handler');
    console.log('📝 Input parameters:', { 
      code: code.substring(0, 30) + '...', 
      redirectUri, 
      apiBaseUrl: environment.apiPrefix.rootPath 
    });
    
    // Enhanced redirect URI debugging
    console.log('🔗 Redirect URI Analysis:', {
      providedRedirectUri: redirectUri,
      currentWindowLocation: window.location.href,
      envRedirectUri: import.meta.env.VITE_MICROSOFT_REDIRECT_URI,
      urlMatch: redirectUri === import.meta.env.VITE_MICROSOFT_REDIRECT_URI,
      windowOrigin: window.location.origin,
      windowPathname: window.location.pathname,
      constructedUri: window.location.origin + window.location.pathname
    });
    
    const request: MicrosoftAuthRequest = {
      code,
      redirect_uri: redirectUri,
    };

    console.log('📤 Request payload structure:', {
      code: !!request.code,
      redirect_uri: request.redirect_uri,
      codeLength: request.code.length
    });

    try {
      const authResponse = await OAuthClient.makeRequest<AuthResponse>('api/Auth/oauth/microsoft', request);
      console.log('✅ Microsoft OAuth Handler Success:', { 
        hasToken: !!authResponse.token, 
        hasUser: !!authResponse.user,
        userEmail: authResponse.user?.email 
      });
      
      console.groupEnd();
      return authResponse;
    } catch (error) {
      console.error('❌ Microsoft OAuth Handler Error:', error);
      console.groupEnd();
      throw error;
    }
  }
}

// Legacy export for backward compatibility
export const handleGoogleCallback = OAuthClient.handleGoogleCallback;
export const handleMicrosoftCallback = OAuthClient.handleMicrosoftCallback;

// React Query hooks for OAuth
export function useGoogleOAuth(
  options?: Omit<UseMutationOptions<AuthResponse, ApiError, { code: string; redirectUri: string }>, 'mutationFn'>
) {
  return useMutation<AuthResponse, ApiError, { code: string; redirectUri: string }>({
    mutationKey: ['auth', 'google'],
    mutationFn: ({ code, redirectUri }) => OAuthClient.handleGoogleCallback(code, redirectUri),
    ...options,
  })
}

export function useMicrosoftOAuth(
  options?: Omit<UseMutationOptions<AuthResponse, ApiError, { code: string; redirectUri: string }>, 'mutationFn'>
) {
  return useMutation<AuthResponse, ApiError, { code: string; redirectUri: string }>({
    mutationKey: ['auth', 'microsoft'],
    mutationFn: ({ code, redirectUri }) => OAuthClient.handleMicrosoftCallback(code, redirectUri),
    ...options,
  })
} 